import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase, getProfile, withTimeout } from '../lib/supabase';
import type { Profile, Dozvola } from '../lib/supabase';
import { ocistiZabrane } from '../lib/zabrane';

const AUTH_TIMEOUT_MS = 15000;

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  /** Sesija je otvorena, ali profil se nije mogao učitati (npr. ne postoji red u profiles). */
  profileMissing: boolean;
  signOut: () => Promise<void>;
  /** Ima li prijavljeni korisnik zadanu dozvolu. Neaktivan nalog nema nijednu. */
  moze: (dozvola: Dozvola) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profilStanje, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileMissing, setProfileMissing] = useState(false);

  // Profil drugog korisnika (npr. prethodno prijavljenog na istom uređaju) se
  // nikad ne smije koristiti dok se ne učita profil trenutnog — inače bi se
  // 1–2 sekunde prikazale tuđe dozvole i stranice. Zato važi samo ako mu id
  // odgovara prijavljenom korisniku.
  const profile = profilStanje && user && profilStanje.id === user.id ? profilStanje : null;

  // Id korisnika za kojeg je zadnje zatražen profil. Odjava pa brza prijava
  // drugim nalogom zna pokrenuti dva ucitajProfil poziva zaredom (stari za
  // korisnika koji se odjavio, novi za onog koji se upravo prijavio) — ako
  // stari (npr. usporen "auth lock", pozadinski tab) settle-uje POSLIJE
  // novog, njegov setProfile prepiše tuđe dozvole preko trenutno prijavljenog
  // korisnika. Ref umjesto state jer poređenje mora vidjeti najsvježiju
  // vrijednost odmah čim se poziv vrati, ne tek na sljedećem renderu.
  const trenutniKorisnik = useRef<string | null>(null);

  useEffect(() => {
    let otkazano = false;

    // Profil se učitava odvojeno od `loading` — ako ovaj poziv ikad zavisi
    // (spor upit, mrežni problem), ne smije zaglaviti cijelu app na spinneru.
    // PrivateRoute već ima poseban prikaz za "profil se učitava".
    const ucitajProfil = async (userId: string) => {
      if (trenutniKorisnik.current !== userId) {
        ocistiZabrane();
        setProfile(null);
        setProfileMissing(false);
      }
      trenutniKorisnik.current = userId;
      try {
        const p = await withTimeout(getProfile(userId), AUTH_TIMEOUT_MS, 'getProfile');
        if (otkazano || trenutniKorisnik.current !== userId) return;
        setProfile(p);
        setProfileMissing(p === null);
      } catch (err) {
        console.error('getProfile (timeout ili greška):', err);
        if (otkazano || trenutniKorisnik.current !== userId) return;
        setProfile(null);
        setProfileMissing(true);
      }
    };

    // Provjeri postojeću sesiju pri pokretanju
    withTimeout(supabase.auth.getSession(), AUTH_TIMEOUT_MS, 'getSession')
      .then(({ data: { session } }) => {
        if (otkazano) return;
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) ucitajProfil(session.user.id);
      })
      .catch(err => {
        console.error('getSession (timeout ili greška):', err);
        if (!otkazano) setLoading(false);
      });

    // Slušaj promjene auth stanja (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (otkazano) return;
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          ucitajProfil(session.user.id);
        } else {
          trenutniKorisnik.current = null;
          setProfile(null);
          setProfileMissing(false);
        }
      }
    );

    return () => {
      otkazano = true;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Ovo je samo za prikaz — pravu provjeru radi backend pri svakom pozivu.
  const moze = (dozvola: Dozvola): boolean =>
    Boolean(profile && profile.aktivan !== false && profile[dozvola]);

  return (
    <AuthContext.Provider value={{ user, profile, loading, profileMissing, signOut, moze }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth mora biti unutar AuthProvider');
  return ctx;
};
